import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR } from "../lib/format";
import { toast } from "sonner";
import {
    FileUp, Plus, Trash2, Edit3, X, Check, ChevronRight, Landmark,
    ShieldCheck, AlertTriangle, Search, Loader2, Sparkles, GitBranch, Tag, RefreshCw, UserPlus,
} from "lucide-react";

const ACCOUNT_TYPE_LABEL = {
    current: "Current Account", savings: "Savings Account",
    od: "Overdraft / CC", cc: "Cash Credit", term_loan: "Term Loan", home_loan: "Home Loan",
};

const fmtDateShort = (s) => {
    if (!s) return "—";
    try {
        const d = new Date(s);
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
    } catch { return s; }
};

export default function BankImport() {
    const [accounts, setAccounts] = useState([]);
    const [selected, setSelected] = useState(null);        // account_id
    const [txns, setTxns] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [txnEdit, setTxnEdit] = useState(null);          // txn being added / edited
    const [actionBusy, setActionBusy] = useState(null);    // 'categorise' | 'pos' | 'receipts' | 'vendors' | null
    const [selectedIds, setSelectedIds] = useState([]);    // multi-select for bulk category
    const [bulkCat, setBulkCat] = useState("");            // bulk category dropdown
    const [availableCats, setAvailableCats] = useState([]);
    const [vendorResult, setVendorResult] = useState(null); // last vendor auto-creation result

    const loadAccounts = async () => {
        try {
            const r = await api.get("/bank-import/accounts-summary");
            setAccounts(r.data.accounts || []);
            if (!selected && r.data.accounts?.length > 0) {
                setSelected(r.data.accounts[0].id);
            }
        } catch (e) {
            toast.error("Failed to load accounts");
        }
    };

    const loadTxns = async () => {
        if (!selected) { setTxns([]); setCategories([]); return; }
        try {
            const [tRes, cRes] = await Promise.all([
                api.get(`/bank-import/txns?account_id=${selected}&limit=5000`),
                api.get(`/bank-import/category-breakdown?account_id=${selected}`),
            ]);
            setTxns(tRes.data.txns || []);
            setCategories(cRes.data.categories || []);
        } catch (e) {
            toast.error("Failed to load transactions");
        }
    };

    useEffect(() => { loadAccounts(); }, []);
    useEffect(() => { loadTxns(); setSelectedIds([]); }, [selected]);
    useEffect(() => {
        api.get("/bank-import/categories").then((r) => setAvailableCats(r.data.categories || [])).catch(() => {});
    }, []);

    const selectedAccount = accounts.find((a) => a.id === selected);
    const filteredTxns = txns.filter((t) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return (t.narration || "").toLowerCase().includes(s)
            || (t.ref || "").toLowerCase().includes(s)
            || String(t.amount || "").includes(s)
            || (t.date || "").includes(s);
    });

    const totalDebits = filteredTxns.reduce((s, t) => s + (t.debit || 0), 0);
    const totalCredits = filteredTxns.reduce((s, t) => s + (t.credit || 0), 0);

    const deleteTxn = async (id) => {
        if (!window.confirm("Delete this transaction? This cannot be undone.")) return;
        try {
            await api.delete(`/bank-import/txns/${id}`);
            toast.success("Transaction deleted");
            loadTxns();
            loadAccounts();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const wipeAllTxnsForAccount = async () => {
        if (!selectedAccount) return;
        if (!window.confirm(`Delete ALL ${selectedAccount.txn_count} transactions for ${selectedAccount.bank_name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/bank-import/txns?account_id=${selected}`);
            toast.success("Cleared all transactions for this account");
            loadTxns(); loadAccounts();
        } catch (e) {
            toast.error("Failed to wipe");
        }
    };

    const runAutoCategorise = async (useAI = false) => {
        if (!selected) return;
        setActionBusy("categorise");
        try {
            const r = await api.post("/bank-import/auto-categorise", {
                account_id: selected,
                only_uncategorised: !useAI, // if AI, re-run everything
                use_ai_fallback: useAI,
            });
            const d = r.data;
            toast.success(`Categorised ${d.rule_based + d.ai_based} of ${d.processed}${useAI ? ` (AI: ${d.ai_based})` : ""}`);
            loadTxns();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActionBusy(null);
        }
    };

    const runReconcilePOs = async () => {
        if (!selected) return;
        setActionBusy("pos");
        try {
            const r = await api.post("/bank-import/reconcile-pos", { account_id: selected, only_unlinked: true });
            toast.success(`Matched ${r.data.matched} debit txns to POs${r.data.note ? " · " + r.data.note : ""}`);
            loadTxns();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActionBusy(null);
        }
    };

    const runMapReceipts = async () => {
        if (!selected) return;
        setActionBusy("receipts");
        try {
            const r = await api.post("/bank-import/map-receipts", {
                account_id: selected, only_unmapped: true, update_project_collected: true,
            });
            toast.success(`Mapped ${r.data.mapped} credits to clients · ${r.data.projects_updated} projects updated`);
            loadTxns();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActionBusy(null);
        }
    };

    const runAutoVendors = async () => {
        if (!selected) return;
        setActionBusy("vendors");
        try {
            const r = await api.post("/bank-import/auto-create-vendors", {
                account_id: selected,
                min_amount: 5000,
                min_txn_count: 1,
            });
            const d = r.data;
            setVendorResult(d);
            toast.success(`Created ${d.created} suspect vendors from ${d.candidates_examined} candidates`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActionBusy(null);
        }
    };

    const applyBulkCategory = async () => {
        if (selectedIds.length === 0) return toast.error("Select rows first");
        if (!bulkCat) return toast.error("Pick a category");
        try {
            const r = await api.patch("/bank-import/txns/bulk-category", {
                txn_ids: selectedIds,
                category: bulkCat,
            });
            toast.success(`Updated ${r.data.updated} transactions → ${r.data.category}`);
            setSelectedIds([]);
            setBulkCat("");
            loadTxns();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk update failed");
        }
    };

    const toggleRow = (id) => {
        setSelectedIds((cur) =>
            cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
        );
    };

    const toggleAllVisible = () => {
        const visibleIds = filteredTxns.map((t) => t.id);
        const allSelected = visibleIds.every((id) => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds((cur) => cur.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedIds((cur) => Array.from(new Set([...cur, ...visibleIds])));
        }
    };

    return (
        <div className="space-y-4" data-testid="bank-import-page">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// BANK IMPORT · MULTI-ACCOUNT</div>
                <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Bank Statement Import</h1>
                <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Upload PDF · CSV · Excel · Per-Account storage — never merged
                </div>
            </div>

            {/* Accounts strip */}
            <div className="flex flex-wrap gap-2">
                {accounts.map((a) => (
                    <button
                        key={a.id}
                        data-testid={`account-pill-${a.id}`}
                        onClick={() => setSelected(a.id)}
                        className={`flex flex-col items-start px-4 py-2 border transition-colors text-left ${
                            selected === a.id
                                ? "border-[var(--brand-amber)] bg-[var(--brand-amber)]/10 text-[var(--text-primary)]"
                                : "border-[var(--border)] hover:border-[var(--brand-amber)]/50 text-[var(--text-secondary)]"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Landmark size={14} className="text-[var(--brand-amber)]" />
                            <span className="font-mono-fin text-[11px] font-bold uppercase">
                                {a.bank_name} · {a.id}
                            </span>
                        </div>
                        <div className="font-mono-fin text-[10px] text-[var(--text-secondary)] mt-0.5">
                            {a.account_no ? `A/C ${a.account_no}` : "no A/C"} · {a.txn_count} txns
                        </div>
                        <div className="font-mono-fin text-[11px] mt-0.5">
                            Bal <span className="text-[var(--brand-amber)]">{fmtINR(a.latest_balance || 0)}</span>
                        </div>
                    </button>
                ))}
                <button
                    data-testid="btn-upload-statement"
                    onClick={() => setUploadOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-[var(--brand-amber)] text-[var(--brand-amber)] font-mono-fin text-[11px] uppercase hover:bg-[var(--brand-amber)]/10 tracking-wider"
                >
                    <FileUp size={14} /> Upload Statement
                </button>
            </div>

            {!selectedAccount && (
                <div className="p-8 border border-dashed border-[var(--border)] text-center">
                    <FileUp size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
                    <div className="text-sm text-[var(--text-secondary)]">No bank accounts yet.</div>
                    <div className="text-xs font-mono-fin text-[var(--text-muted)] mt-1">Upload a PDF statement to auto-create your first account.</div>
                    <button
                        data-testid="btn-empty-upload"
                        onClick={() => setUploadOpen(true)}
                        className="mt-4 px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin text-[11px] uppercase font-bold"
                    >
                        <FileUp size={12} className="inline mr-1" /> Upload First Statement
                    </button>
                </div>
            )}

            {selectedAccount && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard label="TXN COUNT" value={selectedAccount.txn_count} format="raw" sub={`${selectedAccount.bank_name} · ${selectedAccount.id}`} />
                        <KpiCard label="LATEST BALANCE" value={selectedAccount.latest_balance || 0} sub={`as of ${selectedAccount.latest_txn_date || "—"}`} />
                        <KpiCard label="TOTAL DEBITS" value={selectedAccount.total_debits || 0} sub="all periods" />
                        <KpiCard label="TOTAL CREDITS" value={selectedAccount.total_credits || 0} sub="all periods" />
                    </div>

                    {/* AI Automation strip */}
                    <Panel title="Auto-Reconcile Actions ✨" subtitle="One-click categorisation, PO matching, client-receipt mapping & vendor extraction">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <ActionButton
                                testId="btn-auto-categorise"
                                icon={Tag}
                                title="Auto-Categorise"
                                desc="Rule-based tagging into Vendor/Salary/GST/EMI/Charges/Client Receipt/etc"
                                busy={actionBusy === "categorise"}
                                onClick={() => runAutoCategorise(false)}
                                secondary={{
                                    label: "+ AI",
                                    onClick: () => runAutoCategorise(true),
                                    icon: Sparkles,
                                }}
                            />
                            <ActionButton
                                testId="btn-reconcile-pos"
                                icon={GitBranch}
                                title="Reconcile with POs"
                                desc="Match debit RTGS/NEFT to open POs by amount + vendor name"
                                busy={actionBusy === "pos"}
                                onClick={runReconcilePOs}
                            />
                            <ActionButton
                                testId="btn-map-receipts"
                                icon={RefreshCw}
                                title="Map Client Receipts"
                                desc="Link credit txns to client projects & auto-update project.collected"
                                busy={actionBusy === "receipts"}
                                onClick={runMapReceipts}
                            />
                            <ActionButton
                                testId="btn-auto-vendors"
                                icon={UserPlus}
                                title="Extract Suspect Vendors"
                                desc="Auto-create vendor masters from unmapped debit narrations"
                                busy={actionBusy === "vendors"}
                                onClick={runAutoVendors}
                            />
                        </div>
                    </Panel>

                    {vendorResult && (
                        <Panel title="Suspect Vendors Created" subtitle={`${vendorResult.created} new vendor masters from ${vendorResult.candidates_examined} candidates`} right={
                            <button
                                data-testid="btn-dismiss-vendor-result"
                                onClick={() => setVendorResult(null)}
                                className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase hover:text-[var(--text-primary)]"
                            ><X size={12} className="inline" /> Dismiss</button>
                        }>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[240px] overflow-auto">
                                {(vendorResult.sample_created || []).map((v) => (
                                    <div key={v.id} className="p-2 border border-[var(--border)] bg-[var(--surface)]/40" data-testid={`suspect-vendor-${v.id}`}>
                                        <div className="text-xs font-bold">{v.name}</div>
                                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mt-0.5">
                                            {v.id} · {v.txn_count} txn · {fmtINR(v.total_amount)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {vendorResult.created === 0 && (
                                <div className="text-xs text-[var(--text-secondary)] p-2">
                                    No new candidates found. Either narrations are too generic to extract vendor names, or all likely vendors are already in your master.
                                </div>
                            )}
                        </Panel>
                    )}

                    {categories.length > 0 && (
                        <Panel title="Category Breakdown" subtitle={`Total ${categories.reduce((s, c) => s + c.count, 0)} transactions tagged`}>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                {categories.map((c) => (
                                    <div key={c.category} className="p-2 border border-[var(--border)] bg-[var(--surface)]/40" data-testid={`cat-tile-${c.category}`}>
                                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider truncate">{c.category}</div>
                                        <div className="text-xs font-bold mt-0.5">{c.count} txn</div>
                                        {c.total_debit > 0 && (
                                            <div className="text-[10px] text-[var(--neg)] font-mono-fin">-{fmtINR(c.total_debit)}</div>
                                        )}
                                        {c.total_credit > 0 && (
                                            <div className="text-[10px] text-[var(--pos)] font-mono-fin">+{fmtINR(c.total_credit)}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Panel>
                    )}

                    <Panel
                        title={`Transactions · ${selectedAccount.bank_name} · A/C ${selectedAccount.account_no || "—"}`}
                        subtitle={`IFSC ${selectedAccount.ifsc || "—"} · ${selectedAccount.branch_name || ""}`}
                        right={
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Search size={12} className="absolute left-2 top-2 text-[var(--text-secondary)]" />
                                    <input
                                        data-testid="input-search-txns"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Search narration / ref / amount…"
                                        className="pl-7 pr-2 py-1 bg-[var(--surface)] border border-[var(--border)] text-xs font-mono-fin w-56"
                                    />
                                </div>
                                <button
                                    data-testid="btn-add-txn"
                                    onClick={() => setTxnEdit({ account_id: selected, date: new Date().toISOString().slice(0, 10), narration: "", ref: "", debit: 0, credit: 0, balance: 0 })}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[10px] uppercase font-bold"
                                >
                                    <Plus size={12} /> Add Txn
                                </button>
                                <button
                                    data-testid="btn-wipe-account"
                                    onClick={wipeAllTxnsForAccount}
                                    className="flex items-center gap-1 px-2 py-1.5 border border-[var(--neg)] text-[var(--neg)] font-mono-fin text-[10px] uppercase"
                                    title="Wipe all transactions for this account"
                                >
                                    <Trash2 size={12} /> Wipe
                                </button>
                            </div>
                        }
                    >
                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mb-2 uppercase tracking-wider">
                            Showing {filteredTxns.length} of {txns.length} · Debits {fmtINR(totalDebits)} · Credits {fmtINR(totalCredits)}
                        </div>

                        {selectedIds.length > 0 && (
                            <div
                                data-testid="bulk-category-bar"
                                className="flex items-center gap-2 p-2 mb-2 border border-[var(--brand-amber)] bg-[var(--brand-amber)]/10"
                            >
                                <span className="text-[11px] font-mono-fin uppercase text-[var(--brand-amber)] font-bold">
                                    {selectedIds.length} row{selectedIds.length !== 1 ? "s" : ""} selected
                                </span>
                                <div className="flex-1" />
                                <span className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Tag as:</span>
                                <select
                                    data-testid="select-bulk-category"
                                    value={bulkCat}
                                    onChange={(e) => setBulkCat(e.target.value)}
                                    className="bg-[var(--surface)] border border-[var(--border)] px-2 py-1 text-xs font-mono-fin"
                                >
                                    <option value="">— pick category —</option>
                                    {availableCats.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <button
                                    data-testid="btn-apply-bulk-category"
                                    onClick={applyBulkCategory}
                                    disabled={!bulkCat}
                                    className="px-3 py-1 bg-[var(--brand-amber)] text-black font-mono-fin text-[10px] uppercase font-bold disabled:opacity-50"
                                >
                                    Apply
                                </button>
                                <button
                                    data-testid="btn-clear-selection"
                                    onClick={() => setSelectedIds([])}
                                    className="px-2 py-1 border border-[var(--border)] text-[var(--text-secondary)] font-mono-fin text-[10px] uppercase"
                                >
                                    <X size={10} className="inline" /> Clear
                                </button>
                            </div>
                        )}
                        <div className="overflow-auto max-h-[70vh]">
                            <table className="w-full text-sm">
                                <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] sticky top-0 bg-[var(--panel)] z-10">
                                    <tr>
                                        <th className="text-left py-2 px-2 w-6">
                                            <input
                                                type="checkbox"
                                                data-testid="chk-select-all"
                                                checked={filteredTxns.length > 0 && filteredTxns.every((t) => selectedIds.includes(t.id))}
                                                onChange={toggleAllVisible}
                                                className="accent-[var(--brand-amber)] cursor-pointer"
                                            />
                                        </th>
                                        <th className="text-left py-2 px-2">Date</th>
                                        <th className="text-left px-2 min-w-[240px]">Narration</th>
                                        <th className="text-left px-2">Category</th>
                                        <th className="text-left px-2">Linked</th>
                                        <th className="text-right px-2">Debit</th>
                                        <th className="text-right px-2">Credit</th>
                                        <th className="text-right px-2">Balance</th>
                                        <th className="text-right px-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTxns.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="text-center py-8 text-[var(--text-secondary)] text-xs">
                                                {txns.length === 0
                                                    ? "No transactions in this account. Upload a statement or add manually."
                                                    : `No transactions matching "${q}"`}
                                            </td>
                                        </tr>
                                    )}
                                    {filteredTxns.map((t) => (
                                        <tr key={t.id} className={`border-b border-[var(--border)] hover:bg-[var(--surface)]/40 ${selectedIds.includes(t.id) ? "bg-[var(--brand-amber)]/10" : ""}`} data-testid={`txn-row-${t.id}`}>
                                            <td className="py-1.5 px-2">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`chk-txn-${t.id}`}
                                                    checked={selectedIds.includes(t.id)}
                                                    onChange={() => toggleRow(t.id)}
                                                    className="accent-[var(--brand-amber)] cursor-pointer"
                                                />
                                            </td>
                                            <td className="py-1.5 px-2 text-xs font-mono-fin">{fmtDateShort(t.date)}</td>
                                            <td className="px-2 text-xs">
                                                <div>{t.narration || "—"}</div>
                                                {t.ref && <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">{t.ref}</div>}
                                            </td>
                                            <td className="px-2 text-[10px]">
                                                {t.category ? (
                                                    <span className={`px-1.5 py-0.5 font-mono-fin uppercase ${
                                                        t.category === "Uncategorised"
                                                            ? "bg-[var(--surface)] text-[var(--text-muted)]"
                                                            : t.categorised_by === "ai"
                                                              ? "bg-[var(--brand-amber)]/20 text-[var(--brand-amber)]"
                                                              : "bg-[var(--pos)]/20 text-[var(--pos)]"
                                                    }`}>
                                                        {t.category}
                                                    </span>
                                                ) : (
                                                    <span className="text-[var(--text-muted)]">—</span>
                                                )}
                                            </td>
                                            <td className="px-2 text-[10px]">
                                                {t.linked_client_name && (
                                                    <div className="text-[var(--brand-amber)] truncate max-w-[160px]" title={t.linked_client_name}>{t.linked_client_name}</div>
                                                )}
                                                {t.linked_project_name && (
                                                    <div className="text-[var(--text-secondary)] truncate max-w-[160px]" title={t.linked_project_name}>{t.linked_project_name}</div>
                                                )}
                                                {t.linked_po_no && (
                                                    <div className="text-[var(--pos)]">PO {t.linked_po_no}</div>
                                                )}
                                                {!t.linked_client_name && !t.linked_po_no && (
                                                    <span className="text-[var(--text-muted)]">—</span>
                                                )}
                                            </td>
                                            <td className="px-2 text-right text-xs font-mono-fin text-[var(--neg)]">
                                                {t.debit > 0 ? fmtINR(t.debit) : ""}
                                            </td>
                                            <td className="px-2 text-right text-xs font-mono-fin text-[var(--pos)]">
                                                {t.credit > 0 ? fmtINR(t.credit) : ""}
                                            </td>
                                            <td className="px-2 text-right text-xs font-mono-fin text-[var(--brand-amber)]">
                                                {fmtINR(t.balance || 0)}
                                            </td>
                                            <td className="px-2 text-right whitespace-nowrap">
                                                <button
                                                    data-testid={`btn-edit-txn-${t.id}`}
                                                    onClick={() => setTxnEdit(t)}
                                                    className="text-[var(--brand-amber)] p-1 hover:text-[var(--text-primary)]"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={12} />
                                                </button>
                                                <button
                                                    data-testid={`btn-delete-txn-${t.id}`}
                                                    onClick={() => deleteTxn(t.id)}
                                                    className="text-[var(--neg)] p-1 hover:text-[var(--text-primary)] ml-1"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </>
            )}

            {uploadOpen && (
                <UploadWizard
                    accounts={accounts}
                    initialAccountId={selected}
                    onClose={() => setUploadOpen(false)}
                    onDone={() => {
                        setUploadOpen(false);
                        loadAccounts();
                        loadTxns();
                    }}
                />
            )}
            {txnEdit && (
                <TxnEditModal
                    txn={txnEdit}
                    onClose={() => setTxnEdit(null)}
                    onDone={() => {
                        setTxnEdit(null);
                        loadTxns();
                        loadAccounts();
                    }}
                />
            )}
        </div>
    );
}


// ================= UPLOAD WIZARD =================
function UploadWizard({ accounts, initialAccountId, onClose, onDone }) {
    const [step, setStep] = useState(1);       // 1=file, 2=account_selection, 3=preview, 4=done
    const [file, setFile] = useState(null);
    const [detecting, setDetecting] = useState(false);
    const [detected, setDetected] = useState(null);
    const [chosenAccountId, setChosenAccountId] = useState(initialAccountId || null);
    const [createNew, setCreateNew] = useState(false);
    const [newAccountForm, setNewAccountForm] = useState({});
    const [preview, setPreview] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [committed, setCommitted] = useState(null);
    const fileRef = useRef(null);

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setDetecting(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/bank-import/detect-account", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDetected(r.data);
            // Auto-pick best match or default to create new
            if (r.data.existing_matches?.length > 0 && r.data.existing_matches[0].match_score >= 60) {
                setChosenAccountId(r.data.existing_matches[0].id);
                setCreateNew(false);
            } else if (r.data.suggested_new_account) {
                setNewAccountForm(r.data.suggested_new_account);
                setCreateNew(true);
                setChosenAccountId(null);
            }
            setStep(2);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Detection failed");
        } finally {
            setDetecting(false);
        }
    };

    const doCreateAccount = async () => {
        try {
            const r = await api.post("/bank-import/create-account", newAccountForm);
            toast.success(`Created ${r.data.account.bank_name} · ${r.data.account.id}`);
            setChosenAccountId(r.data.account.id);
            setCreateNew(false);
            return r.data.account.id;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
            return null;
        }
    };

    const doPreview = async () => {
        let accId = chosenAccountId;
        if (createNew) {
            accId = await doCreateAccount();
            if (!accId) return;
        }
        if (!accId) return toast.error("Pick an account first");
        setDetecting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("account_id", accId);
            const r = await api.post("/bank-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview({ ...r.data, account_id: accId });
            setStep(3);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Preview failed");
        } finally {
            setDetecting(false);
        }
    };

    const doCommit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const r = await api.post("/bank-import/commit", {
                account_id: preview.account_id,
                filename: file?.name || "upload.pdf",
                txns: preview.txns || [],
                skip_duplicates: true,
            });
            setCommitted(r.data);
            setStep(4);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Commit failed");
        } finally {
            setCommitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/80 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto" data-testid="modal-upload-wizard">
            <div className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-4xl">
                <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
                    <div>
                        <div className="font-mono-fin text-[10px] uppercase tracking-wider text-[var(--brand-amber)]">Step {step} of 4</div>
                        <h3 className="font-display text-xl font-bold mt-0.5">
                            {step === 1 && "1. Upload bank statement"}
                            {step === 2 && "2. Attach to a bank account"}
                            {step === 3 && "3. Preview & confirm transactions"}
                            {step === 4 && "4. Done"}
                        </h3>
                    </div>
                    <button onClick={onClose} data-testid="btn-close-wizard" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {step === 1 && (
                        <div>
                            <div className="text-sm text-[var(--text-secondary)] mb-4">
                                Supported formats: <span className="font-mono-fin text-[var(--brand-amber)]">PDF · Excel · CSV</span>.
                                We'll auto-detect the bank and A/C number from the file. Multiple statements for the same account can be uploaded — duplicates are skipped.
                            </div>
                            <div
                                onClick={() => fileRef.current?.click()}
                                className="border-2 border-dashed border-[var(--border)] hover:border-[var(--brand-amber)] p-16 text-center cursor-pointer transition-colors"
                                data-testid="dropzone-upload"
                            >
                                {detecting ? (
                                    <><Loader2 size={32} className="animate-spin mx-auto text-[var(--brand-amber)]" />
                                    <div className="mt-2 font-mono-fin text-sm">Detecting bank & account…</div></>
                                ) : (
                                    <><FileUp size={40} className="mx-auto text-[var(--brand-amber)] mb-3" />
                                    <div className="font-mono-fin text-sm text-[var(--text-primary)]">Drop bank statement here or click to browse</div>
                                    <div className="font-mono-fin text-[10px] text-[var(--text-secondary)] mt-2">Max 20 MB · PDF / Excel / CSV</div></>
                                )}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf,.xlsx,.xls,.csv,.txt"
                                    className="hidden"
                                    onChange={onFileChange}
                                    data-testid="input-file-upload"
                                />
                            </div>
                        </div>
                    )}

                    {step === 2 && detected && (
                        <div>
                            <div className="p-3 border border-[var(--border)] bg-[var(--surface)]/40 mb-4">
                                <div className="font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] mb-1">Detected from PDF</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    <div><span className="text-[var(--text-secondary)]">Bank:</span> <b>{detected.meta?.bank || "—"}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">A/C No:</span> <b className="font-mono-fin">{detected.meta?.account_no || "—"}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">IFSC:</span> <b className="font-mono-fin">{detected.meta?.ifsc || "—"}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">Branch:</span> <b>{detected.meta?.branch || "—"}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">Holder:</span> <b>{detected.meta?.holder || "—"}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">Period:</span> <b>{detected.meta?.period_from} → {detected.meta?.period_to}</b></div>
                                    <div><span className="text-[var(--text-secondary)]">Pages:</span> <b>{detected.meta?.total_pages || "—"}</b></div>
                                </div>
                                {detected.meta?.warnings && (
                                    <div className="mt-2 text-[11px] text-[var(--brand-amber)]">
                                        <AlertTriangle size={10} className="inline mr-1" />{detected.meta.warnings}
                                    </div>
                                )}
                            </div>

                            {detected.existing_matches?.length > 0 && (
                                <div className="mb-4">
                                    <div className="font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] mb-2">Existing matches</div>
                                    {detected.existing_matches.map((m) => (
                                        <label
                                            key={m.id}
                                            data-testid={`account-choice-${m.id}`}
                                            className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors mb-2 ${
                                                !createNew && chosenAccountId === m.id
                                                    ? "border-[var(--brand-amber)] bg-[var(--brand-amber)]/10"
                                                    : "border-[var(--border)] hover:border-[var(--brand-amber)]/50"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="account"
                                                checked={!createNew && chosenAccountId === m.id}
                                                onChange={() => { setCreateNew(false); setChosenAccountId(m.id); }}
                                            />
                                            <div className="flex-1">
                                                <div className="text-sm font-bold">{m.bank_name} · A/C {m.account_no || "—"}</div>
                                                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)]">
                                                    {m.id} · IFSC {m.ifsc || "—"} · {m.txn_count} existing txns
                                                </div>
                                            </div>
                                            <div className="text-[10px] font-mono-fin text-[var(--pos)]">MATCH {m.match_score}%</div>
                                        </label>
                                    ))}
                                </div>
                            )}

                            <div className="mb-4">
                                <label
                                    data-testid="account-choice-new"
                                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                                        createNew
                                            ? "border-[var(--brand-amber)] bg-[var(--brand-amber)]/10"
                                            : "border-[var(--border)] hover:border-[var(--brand-amber)]/50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="account"
                                        checked={createNew}
                                        onChange={() => { setCreateNew(true); setChosenAccountId(null); }}
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-bold">➕ Create new bank account with detected details</div>
                                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)]">
                                            Suggested id: {detected.suggested_new_account?.id} · {detected.suggested_new_account?.bank_name}
                                        </div>
                                    </div>
                                </label>
                                {createNew && detected.suggested_new_account && (
                                    <div className="mt-3 p-3 border border-[var(--border)] bg-[var(--surface)]/40 grid grid-cols-2 gap-3">
                                        {["id","bank_name","account_no","ifsc","branch_name","account_name","type","purpose","opening_balance"].map((k) => (
                                            <div key={k}>
                                                <label className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">{k.replace(/_/g," ")}</label>
                                                <input
                                                    data-testid={`input-new-${k}`}
                                                    value={newAccountForm[k] ?? ""}
                                                    onChange={(e) => setNewAccountForm({ ...newAccountForm, [k]: k === "opening_balance" ? Number(e.target.value || 0) : e.target.value })}
                                                    className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1 text-xs font-mono-fin mt-0.5"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between pt-2 border-t border-[var(--border)]">
                                <button data-testid="btn-back-1" onClick={() => setStep(1)} className="px-4 py-2 border border-[var(--border)] text-xs font-mono-fin uppercase">← Back</button>
                                <button
                                    data-testid="btn-next-preview"
                                    onClick={doPreview}
                                    disabled={detecting || (!chosenAccountId && !createNew)}
                                    className="px-6 py-2 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold disabled:opacity-50 flex items-center gap-2"
                                >
                                    {detecting ? <><Loader2 size={12} className="animate-spin"/> Parsing…</> : <>Parse & Preview <ChevronRight size={12}/></>}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && preview && (
                        <div>
                            <div className="grid grid-cols-4 gap-3 mb-4">
                                <StatCard label="Parsed" value={preview.parsed} />
                                <StatCard label="New (unique)" value={preview.new} tone="pos" />
                                <StatCard label="Duplicates" value={preview.duplicates} tone="warn" />
                                <StatCard label="Pages" value={preview.meta?.pdf_pages_parsed || preview.meta?.total_pages || "—"} />
                            </div>
                            <div className="p-3 border border-[var(--border)] bg-[var(--surface)]/40 text-xs mb-3">
                                {preview.meta?.bank} · A/C {preview.meta?.account_no || "—"} · Committing to <b>{preview.account_id}</b>
                                {preview.duplicates > 0 && (
                                    <span className="ml-3 text-[var(--brand-amber)]">
                                        <ShieldCheck size={10} className="inline mr-1" />
                                        {preview.duplicates} duplicates will be skipped automatically
                                    </span>
                                )}
                            </div>
                            <div className="max-h-[45vh] overflow-y-auto border border-[var(--border)]">
                                <table className="w-full text-sm">
                                    <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] sticky top-0 bg-[var(--panel)]">
                                        <tr>
                                            <th className="text-left py-2 px-2">Date</th>
                                            <th className="text-left px-2">Narration</th>
                                            <th className="text-right px-2">Debit</th>
                                            <th className="text-right px-2">Credit</th>
                                            <th className="text-right px-2">Balance</th>
                                            <th className="text-center px-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.txns || []).slice(0, 50).map((t, i) => (
                                            <tr key={i} className={`border-b border-[var(--border)] ${t.is_duplicate ? "opacity-40" : ""}`}>
                                                <td className="py-1 px-2 text-xs font-mono-fin">{fmtDateShort(t.date)}</td>
                                                <td className="px-2 text-xs">{(t.narration || "").slice(0, 80)}</td>
                                                <td className="px-2 text-right text-xs text-[var(--neg)] font-mono-fin">{t.debit > 0 ? fmtINR(t.debit) : ""}</td>
                                                <td className="px-2 text-right text-xs text-[var(--pos)] font-mono-fin">{t.credit > 0 ? fmtINR(t.credit) : ""}</td>
                                                <td className="px-2 text-right text-xs text-[var(--brand-amber)] font-mono-fin">{fmtINR(t.balance || 0)}</td>
                                                <td className="px-2 text-center text-[9px] font-mono-fin uppercase">
                                                    {t.is_duplicate ? <span className="text-[var(--brand-amber)]">DUP</span> : <span className="text-[var(--pos)]">NEW</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {preview.txns?.length > 50 && (
                                    <div className="text-center text-[10px] font-mono-fin text-[var(--text-muted)] py-2">
                                        … showing first 50 of {preview.txns.length}
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-between pt-4 border-t border-[var(--border)] mt-4">
                                <button data-testid="btn-back-2" onClick={() => setStep(2)} className="px-4 py-2 border border-[var(--border)] text-xs font-mono-fin uppercase">← Back</button>
                                <button
                                    data-testid="btn-commit-import"
                                    onClick={doCommit}
                                    disabled={committing || preview.new === 0}
                                    className="px-6 py-2 bg-[var(--pos)] text-black text-xs font-mono-fin uppercase font-bold disabled:opacity-50 flex items-center gap-2"
                                >
                                    {committing ? <><Loader2 size={12} className="animate-spin"/> Committing…</> : <><Check size={12}/> Commit {preview.new} Transactions</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 4 && committed && (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto rounded-full bg-[var(--pos)]/20 flex items-center justify-center mb-4">
                                <Check size={32} className="text-[var(--pos)]" />
                            </div>
                            <h3 className="font-display text-2xl font-bold mb-2">Import complete!</h3>
                            <div className="text-sm text-[var(--text-secondary)] mb-4">
                                {committed.inserted} transactions committed · {committed.skipped_duplicates} duplicates skipped
                            </div>
                            <button
                                data-testid="btn-close-done"
                                onClick={onDone}
                                className="px-6 py-2 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold"
                            >
                                View Transactions
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ================= TXN EDIT MODAL =================
function TxnEditModal({ txn, onClose, onDone }) {
    const [f, setF] = useState({
        account_id: txn.account_id,
        date: txn.date || new Date().toISOString().slice(0, 10),
        value_date: txn.value_date || txn.date || "",
        narration: txn.narration || "",
        ref: txn.ref || "",
        debit: txn.debit || 0,
        credit: txn.credit || 0,
        balance: txn.balance || 0,
    });
    const isEdit = !!txn.id;

    const save = async () => {
        try {
            const body = {
                ...f,
                debit: Number(f.debit) || 0,
                credit: Number(f.credit) || 0,
                balance: Number(f.balance) || 0,
            };
            if (isEdit) {
                await api.put(`/bank-import/txns/${txn.id}`, body);
                toast.success("Transaction updated");
            } else {
                await api.post("/bank-import/txns", body);
                toast.success("Transaction added");
            }
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-edit-txn">
            <div className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-display text-lg font-bold">{isEdit ? "Edit" : "Add"} Bank Transaction</h3>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1"><X size={18}/></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date">
                        <input data-testid="input-txn-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                    <Field label="Value Date">
                        <input data-testid="input-txn-value-date" type="date" value={f.value_date || f.date} onChange={(e) => setF({ ...f, value_date: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                    <Field label="Narration" className="col-span-2">
                        <input data-testid="input-txn-narration" value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })} placeholder="e.g. RTGS to L&T Finance for EMI" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </Field>
                    <Field label="Reference (UTR / cheque / txn id)" className="col-span-2">
                        <input data-testid="input-txn-ref" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin"/>
                    </Field>
                    <Field label="Debit (₹)">
                        <input data-testid="input-txn-debit" type="number" step="0.01" value={f.debit} onChange={(e) => setF({ ...f, debit: e.target.value, credit: e.target.value > 0 ? 0 : f.credit })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm text-[var(--neg)]"/>
                    </Field>
                    <Field label="Credit (₹)">
                        <input data-testid="input-txn-credit" type="number" step="0.01" value={f.credit} onChange={(e) => setF({ ...f, credit: e.target.value, debit: e.target.value > 0 ? 0 : f.debit })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm text-[var(--pos)]"/>
                    </Field>
                    <Field label="Running Balance (₹)" className="col-span-2">
                        <input data-testid="input-txn-balance" type="number" step="0.01" value={f.balance} onChange={(e) => setF({ ...f, balance: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin text-[var(--brand-amber)]"/>
                    </Field>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                    <button data-testid="btn-save-txn" onClick={save} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                        {isEdit ? "Update" : "Add"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function StatCard({ label, value, tone }) {
    const color = tone === "pos" ? "text-[var(--pos)]" : tone === "warn" ? "text-[var(--brand-amber)]" : tone === "neg" ? "text-[var(--neg)]" : "text-[var(--text-primary)]";
    return (
        <div className="p-3 border border-[var(--border)] bg-[var(--surface)]/40">
            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">{label}</div>
            <div className={`text-2xl font-bold mt-1 ${color}`}>{typeof value === "number" ? value.toLocaleString("en-IN") : value}</div>
        </div>
    );
}

function Field({ label, children, className }) {
    return (
        <div className={className}>
            <label className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider mb-1 block">{label}</label>
            {children}
        </div>
    );
}

function ActionButton({ icon: Icon, title, desc, onClick, busy, secondary, testId }) {
    return (
        <div className="p-3 border border-[var(--border)] bg-[var(--surface)]/40 hover:border-[var(--brand-amber)]/60 transition-colors">
            <div className="flex items-start gap-2 mb-2">
                <Icon size={18} className="text-[var(--brand-amber)] mt-0.5" />
                <div className="flex-1">
                    <div className="text-sm font-bold">{title}</div>
                    <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mt-0.5">{desc}</div>
                </div>
            </div>
            <div className="flex gap-1">
                <button
                    data-testid={testId}
                    onClick={onClick}
                    disabled={busy}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin text-[10px] uppercase font-bold disabled:opacity-50"
                >
                    {busy ? <><Loader2 size={10} className="animate-spin" /> Running…</> : <>Run</>}
                </button>
                {secondary && (
                    <button
                        data-testid={`${testId}-secondary`}
                        onClick={secondary.onClick}
                        disabled={busy}
                        className="flex items-center gap-1 px-3 py-1.5 border border-[var(--brand-amber)] text-[var(--brand-amber)] font-mono-fin text-[10px] uppercase disabled:opacity-50"
                    >
                        {secondary.icon && <secondary.icon size={10} />} {secondary.label}
                    </button>
                )}
            </div>
        </div>
    );
}
